import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useSession } from "@/auth/SessionContext";
import { useOnline } from "@/lib/offline";
import { useLocale } from "@/locale/LocaleContext";
import { LanguageSwitch } from "./LanguageSwitch";
import { Wordmark } from "./Wordmark";
import {
  BarChart,
  ClipboardList,
  Cloud,
  Cpu,
  FolderKanban,
  Landmark,
  LayoutDashboard,
  ListTodo,
  Plug,
  UserRound,
  Wrench,
} from "./icons";
import { StatePanel } from "./StatePanel";
import styles from "./shell.module.css";
import ui from "./ui.module.css";

export function AppShell() {
  const { session, isOperator } = useSession();
  const online = useOnline();
  const { m } = useLocale();

  const tenantLinks = [
    { to: "/app", end: true, label: m.nav.overview, icon: LayoutDashboard, mobile: false },
    { to: "/app/environment", label: m.nav.environment, icon: Cloud, mobile: true },
    { to: "/app/machines", label: m.nav.machines, icon: Cpu, mobile: false },
    { to: "/app/projects", label: m.nav.projects, icon: FolderKanban, mobile: true },
    { to: "/app/tasks", label: m.nav.tasks, icon: ListTodo, mobile: true },
    { to: "/app/integrations", label: m.nav.connections, icon: Plug, mobile: false },
    { to: "/app/account", label: m.nav.account, icon: UserRound, mobile: true },
  ];

  return (
    <div className={`${styles.shell} ${styles.shellDesktop}`}>
      <div className={`${ui.banner} ${styles.bannerFull}`}>{m.common.pilotBanner}</div>
      {!online ? (
        <div className={styles.bannerFull} style={{ padding: "0.75rem 1rem" }}>
          <StatePanel kind="offline" title={m.states.offline} message={m.states.offlineBody} />
        </div>
      ) : null}

      <aside className={styles.side} aria-label={m.nav.primary}>
        <div style={{ marginBottom: "1rem", paddingInline: "0.35rem" }}>
          <Wordmark to="/app" />
        </div>
        {tenantLinks.map(({ to, end, label, icon: Icon }) => (
          <NavLink key={to} to={to} end={end}>
            <Icon size={17} aria-hidden />
            {label}
          </NavLink>
        ))}
        {isOperator ? (
          <NavLink to="/operator">
            <Wrench size={17} aria-hidden />
            {m.nav.operator}
          </NavLink>
        ) : null}
        {session?.user.role === "admin" ? (
          <NavLink to="/admin">
            <Landmark size={17} aria-hidden />
            {m.nav.admin}
          </NavLink>
        ) : null}
        <div style={{ marginTop: "auto", display: "grid", gap: "0.65rem", paddingTop: "1rem" }}>
          <LanguageSwitch />
          <p className={styles.metaLine}>{session?.tenant.name}</p>
        </div>
      </aside>

      <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <header className={styles.top}>
          <div className={styles.topBrandMobile}>
            <Wordmark to="/app" compact />
          </div>
          <div className={styles.desktopNav}>
            <span className={styles.metaLine}>{session?.user.name}</span>
            <span className={`mono ${styles.metaLine}`}>{session?.user.email}</span>
            <LanguageSwitch />
          </div>
          <div className={styles.topMobileTrail}>
            <LanguageSwitch className={styles.mobileLang} />
            <span className={`${styles.metaLine} ${styles.topBrandMobile} ${styles.topTenant}`}>
              {session?.tenant.name}
            </span>
          </div>
        </header>

        <main id="main" className={`${styles.main} fade-in`}>
          <Outlet />
        </main>
      </div>

      <nav className={styles.bottomNav} aria-label={m.nav.sections}>
        {tenantLinks
          .filter((l) => l.mobile)
          .map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                [styles.navItem, isActive ? styles.navItemActive : ""].filter(Boolean).join(" ")
              }
            >
              <Icon size={18} aria-hidden />
              {label}
            </NavLink>
          ))}
      </nav>
    </div>
  );
}

export function OperatorShell() {
  const { session } = useSession();
  const { m } = useLocale();
  return (
    <div className={`${styles.shell} ${styles.shellDesktop}`}>
      <div className={`${ui.banner} ${styles.bannerFull}`}>{m.common.operatorBanner}</div>
      <aside className={styles.side}>
        <div style={{ marginBottom: "1rem", paddingInline: "0.35rem" }}>
          <Wordmark to="/operator" />
        </div>
        <NavLink to="/operator" end>
          <Cpu size={17} aria-hidden />
          {m.nav.fleet}
        </NavLink>
        <NavLink to="/operator/workspace-requests">
          <ClipboardList size={17} aria-hidden />
          {m.nav.workspaceRequests}
        </NavLink>
        {session?.user.role === "admin" ? (
          <NavLink to="/admin">
            <Landmark size={17} aria-hidden />
            {m.nav.admin}
          </NavLink>
        ) : null}
        <NavLink to="/app">
          <LayoutDashboard size={17} aria-hidden />
          {m.nav.cabinet}
        </NavLink>
        <NavLink to="/app/environment">
          <Cloud size={17} aria-hidden />
          {m.nav.environment}
        </NavLink>
        <NavLink to="/app/integrations">
          <Plug size={17} aria-hidden />
          {m.nav.connections}
        </NavLink>
        <NavLink to="/app/account">
          <UserRound size={17} aria-hidden />
          {m.nav.account}
        </NavLink>
        <div style={{ marginTop: "auto", display: "grid", gap: "0.65rem" }}>
          <LanguageSwitch />
          <p className={styles.metaLine}>{session?.user.email}</p>
        </div>
      </aside>
      <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <header className={styles.top}>
          <span className={styles.brand}>{m.nav.operator}</span>
          <div className={styles.topMobileTrail}>
            <LanguageSwitch className={styles.mobileLang} />
            <span className={styles.metaLine}>{session?.user.role}</span>
          </div>
        </header>
        <main id="main" className={`${styles.main} fade-in`}>
          <Outlet />
        </main>
      </div>
      <nav className={styles.bottomNav} aria-label={m.nav.sections}>
        <NavLink to="/operator" end className={({ isActive }) => [styles.navItem, isActive ? styles.navItemActive : ""].filter(Boolean).join(" ")}>
          <Cpu size={18} aria-hidden />
          {m.nav.fleet}
        </NavLink>
        <NavLink to="/operator/workspace-requests" className={({ isActive }) => [styles.navItem, isActive ? styles.navItemActive : ""].filter(Boolean).join(" ")}>
          <ClipboardList size={18} aria-hidden />
          {m.nav.workspaceRequests}
        </NavLink>
        <NavLink to="/app" className={({ isActive }) => [styles.navItem, isActive ? styles.navItemActive : ""].filter(Boolean).join(" ")}>
          <LayoutDashboard size={18} aria-hidden />
          {m.nav.cabinet}
        </NavLink>
        <NavLink to="/app/account" className={({ isActive }) => [styles.navItem, isActive ? styles.navItemActive : ""].filter(Boolean).join(" ")}>
          <UserRound size={18} aria-hidden />
          {m.nav.account}
        </NavLink>
      </nav>
    </div>
  );
}

const adminMenu = [
  { id: "park", label: "park" as const, icon: Cpu },
  { id: "counts", label: "counts" as const, icon: Landmark },
  { id: "audit", label: "audit" as const, icon: ClipboardList },
  { id: "clients", label: "clients" as const, icon: UserRound },
  { id: "macs", label: "macs" as const, icon: BarChart },
  { id: "journal", label: "journal" as const, icon: ListTodo },
  { id: "requests", label: "requests" as const, icon: FolderKanban },
  { id: "settings", label: "settings" as const, icon: Wrench },
  { id: "roles", label: "roles" as const, icon: UserRound },
];

export function AdminShell() {
  const { session } = useSession();
  const { m } = useLocale();
  const { hash } = useLocation();
  return (
    <div className={`${styles.shell} ${styles.shellDesktop}`}>
      <div className={`${ui.banner} ${styles.bannerFull}`}>{m.admin.banner}</div>
      <aside className={styles.side}>
        <div style={{ marginBottom: "1rem", paddingInline: "0.35rem" }}>
          <Wordmark to="/admin" />
        </div>
        {adminMenu.map(({ id, label, icon: Icon }) => (
          <a key={id} href={`#${id}`} aria-current={hash === `#${id}` ? "page" : undefined}>
            <Icon size={17} aria-hidden />
            {m.admin[label]}
          </a>
        ))}
        <div style={{ marginTop: "auto", display: "grid", gap: "0.65rem" }}>
          <LanguageSwitch />
          <p className={styles.metaLine}>{session?.user.email}</p>
        </div>
      </aside>
      <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <header className={styles.top}>
          <span className={styles.brand}>{m.nav.admin}</span>
          <div className={styles.topMobileTrail}>
            <LanguageSwitch className={styles.mobileLang} />
            <span className={styles.metaLine}>{session?.user.role}</span>
          </div>
        </header>
        <main id="main" className={`${styles.main} fade-in`}>
          <Outlet />
        </main>
      </div>
      <nav className={`${styles.bottomNav} ${styles.bottomNavScroll}`} aria-label={m.nav.sections}>
        {adminMenu.map(({ id, label, icon: Icon }) => (
          <a
            key={id}
            href={`#${id}`}
            className={[styles.navItem, hash === `#${id}` ? styles.navItemActive : ""].filter(Boolean).join(" ")}
          >
            <Icon size={18} aria-hidden />
            {m.admin[label]}
          </a>
        ))}
      </nav>
    </div>
  );
}
